import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Moon, Sun, Menu, X, MessageCircleMore } from "lucide-react";
import Container from "./Container";
import { cls } from "../utils/helpers";
import { COMPANY_NAME, WHATSAPP_NUM, DEFAULT_P } from "../utils/constants";

function openWhatsApp() {
  const num = WHATSAPP_NUM.replace(/\D/g, "");
  const text = encodeURIComponent(
    "Bonjour, je souhaite échanger au sujet de vos prestations / software."
  );
  window.open(`https://wa.me/${num}?text=${text}`, "_blank");
}

export default function Navbar({
  route,
  theme = "light",
  setTheme = () => {},
  p = DEFAULT_P,
  lang = "fr",
  setLang = () => {},
  t = (k) => k,
  loginUrl = "https://app.stragilia.com/login.php",
  registerUrl = "https://app.stragilia.com/selectModules.php",
  authNewTab = true,
}) {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAcc, setMobileAcc] = useState(null);

  const closeMenus = () => setOpenMenu(null);
  const closeAll = () => {
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileAcc(null);
  };

  const extLinkProps = authNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {};

  const link = (r, label) => (
    <Link
      to={r}
      className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
      onClick={(e) => {
        e.currentTarget.blur();
        closeAll();
      }}
    >
      {label}
    </Link>
  );

  return (
    <header className={p.header} onMouseLeave={closeMenus}>
      <Container>
        <div className="h-16 flex items-center justify-between gap-4">
          <Link to="/" className={cls("font-bold text-lg tracking-tight", p.headerTitle)}>
            {COMPANY_NAME}
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            <div className="relative">
              <button
                className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                aria-haspopup="true"
                aria-expanded={openMenu === "prestations"}
                onClick={() =>
                  setOpenMenu(openMenu === "prestations" ? null : "prestations")
                }
              >
                {t("nav.prestations")}
              </button>
              {openMenu === "prestations" && (
                <div className="absolute left-0 mt-2 w-[720px] max-w-[90vw] z-50">
                  <div className={cls(p.card, "rounded-2xl p-4 shadow-xl")} onMouseLeave={closeMenus}>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {[
                        {
                          href: "/prestations#presta-direction",
                          label: t("presta.direction"),
                          desc: t("presta.direction.b1"),
                        },
                        {
                          href: "/prestations#presta-pilotage",
                          label: t("presta.pilotage"),
                          desc: t("presta.pilotage.b1"),
                        },
                        {
                          href: "/prestations#presta-etudes",
                          label: t("presta.etudes"),
                          desc: t("presta.etudes.b1"),
                        },
                        {
                          href: "/prestations#presta-suivi",
                          label: t("presta.suivi"),
                          desc: t("presta.suivi.b1"),
                        },
                        {
                          href: "/prestations#presta-optimisation",
                          label: t("presta.optim"),
                          desc: t("presta.optim.b1"),
                        },
                        {
                          href: "/prestations#presta-logistique",
                          label: t("presta.log"),
                          desc: t("presta.log.b2"),
                        },
                      ].map((it) => (
                        <a
                          key={it.href}
                          href={it.href}
                          className="group rounded-xl px-3 py-2 hover:bg-white/50 dark:hover:bg-white/5 transition-colors"
                          onClick={closeAll}
                        >
                          <div className={cls("text-sm font-semibold", p.cardTitle)}>{it.label}</div>
                          <div className={cls("text-xs opacity-80 mt-0.5", p.cardP)}>{it.desc}</div>
                        </a>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-black/5 dark:border-white/10 flex items-center justify-between">
                      <Link
                        to="/prestations"
                        className={cls("text-sm underline underline-offset-4 hover:no-underline", p.breadcrumbLink)}
                        onClick={closeAll}
                      >
                        {t("nav.seeall")} →
                      </Link>
                      <div className="flex gap-2">
                        <Link to="/contact" className={p.ctaSecondary} onClick={closeAll}>
                          {t("btn.contact")}
                        </Link>
                        <Link to="/contact" className={p.ctaPrimary} onClick={closeAll}>
                          {t("nav.quote")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                aria-haspopup="true"
                aria-expanded={openMenu === "software"}
                onClick={() => setOpenMenu(openMenu === "software" ? null : "software")}
              >
                {t("nav.software")}
              </button>
              {openMenu === "software" && (
                <div className="absolute left-0 mt-2 w-64 z-50">
                  <div className={cls(p.card, "rounded-2xl p-2 shadow-xl")} onMouseLeave={closeMenus}>
                    {[
                      { href: "/software#soft-planification", label: t("soft.plan.title") },
                      { href: "/software#soft-collaboration", label: t("soft.collab.title") },
                      { href: "/software#soft-logistique", label: t("soft.log.title") },
                      { href: "/software#soft-qualite", label: t("soft.qual.title") },
                    ].map((it) => (
                      <a
                        key={it.href}
                        href={it.href}
                        className="block rounded-lg px-3 py-2 hover:bg-white/50 dark:hover:bg-white/5 text-sm transition-colors"
                        onClick={closeAll}
                      >
                        {it.label}
                      </a>
                    ))}
                    <div className="mt-1 flex gap-2 px-2 pb-2">
                      <Link to="/software" className={p.chipAlt || p.chip} onClick={closeAll}>
                        {t("nav.seeall")}
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {link("/recrutement", t("nav.recruitment"))}
            {link("/contact", t("nav.contact"))}
          </nav>

          <div className="hidden lg:flex items-center gap-1.5">
            <Link
              to="/contact"
              className="rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 px-3.5 py-2 text-sm font-semibold shadow-sm transition-colors whitespace-nowrap"
              onClick={closeAll}
            >
              {t("nav.quote")}
            </Link>
            <a
              href={loginUrl}
              {...extLinkProps}
              className="rounded-lg hover:bg-black/5 dark:hover:bg-white/10 px-3 py-2 text-sm font-medium transition-colors"
            >
              {t("nav.login")}
            </a>
            <a
              href={registerUrl}
              {...extLinkProps}
              className="rounded-lg hover:bg-black/5 dark:hover:bg-white/10 px-3 py-2 text-sm font-medium transition-colors"
            >
              {t("nav.register")}
            </a>

            <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-0.5"></div>

            <button
              aria-label="Switch language"
              title="Language FR/EN"
              className="rounded-lg hover:bg-black/5 dark:hover:bg-white/10 h-9 px-2.5 text-xs font-semibold transition-colors"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            >
              {lang === "fr" ? "FR" : "EN"}
            </button>
            <button
              aria-label={t("aria.theme")}
              title={t("aria.theme")}
              className="rounded-lg hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center transition-colors h-9 w-9"
              onClick={() => {
                setTheme(theme === "light" ? "dark" : "light");
                closeMenus();
              }}
            >
              <span className="relative block h-4 w-4">
                <Moon
                  className={cls(
                    "absolute inset-0 transition-all duration-300 ease-out",
                    theme === "light" ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-75"
                  )}
                  size={16}
                />
                <Sun
                  className={cls(
                    "absolute inset-0 transition-all duration-300 ease-out",
                    theme === "dark" ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-75"
                  )}
                  size={16}
                />
              </span>
            </button>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <button
              aria-label="Switch language"
              className="rounded-lg ring-1 ring-gray-300 dark:ring-white/20 h-10 px-3 text-xs font-semibold"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            >
              {lang === "fr" ? "FR" : "EN"}
            </button>
            <button
              aria-label={t("aria.theme")}
              title={t("aria.theme")}
              className="rounded-lg ring-1 ring-gray-300 dark:ring-white/20 flex items-center justify-center h-10 w-10"
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            >
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button
              aria-label={mobileOpen ? t("aria.menu.close") : t("aria.menu.open")}
              className="rounded-lg ring-1 ring-gray-300 dark:ring-white/20 flex items-center justify-center h-10 w-10"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </Container>

      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[60] lg:hidden" onClick={closeAll} />
          <div
            className={cls(
              "fixed right-0 top-0 h-screen w-[85%] max-w-sm z-[70] shadow-2xl lg:hidden overflow-y-auto",
              theme === "dark" ? "bg-indigo-950 text-white" : "bg-white text-gray-900"
            )}
            role="dialog"
            aria-modal="true"
          >
            <div className="sticky top-0 bg-inherit border-b border-black/5 dark:border-white/10 px-4 py-3 flex items-center justify-between">
              <span className="font-semibold text-base">{t("mobile.menu")}</span>
              <button
                aria-label={t("aria.menu.close")}
                className="h-10 w-10 rounded-lg ring-1 ring-gray-300 dark:ring-white/20 flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10"
                onClick={closeAll}
              >
                <X size={20} />
              </button>
            </div>

            <nav className="p-4 space-y-2">
              <Link to="/" className="block rounded-lg px-4 py-3 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10" onClick={closeAll}>
                {t("nav.home")}
              </Link>

              <div>
                <button
                  className="w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm font-medium ring-1 ring-gray-300 dark:ring-white/20 hover:bg-black/5 dark:hover:bg-white/10"
                  onClick={() => setMobileAcc(mobileAcc === "prestations" ? null : "prestations")}
                >
                  {t("nav.prestations")} <span className="text-lg">{mobileAcc === "prestations" ? "−" : "+"}</span>
                </button>
                {mobileAcc === "prestations" && (
                  <div className="mt-2 ml-2 space-y-1">
                    {[
                      { href: "/prestations#presta-direction", label: t("presta.direction") },
                      { href: "/prestations#presta-pilotage", label: t("presta.pilotage") },
                      { href: "/prestations#presta-etudes", label: t("presta.etudes") },
                      { href: "/prestations#presta-suivi", label: t("presta.suivi") },
                      { href: "/prestations#presta-optimisation", label: t("presta.optim") },
                      { href: "/prestations#presta-logistique", label: t("presta.log") },
                    ].map((it) => (
                      <a
                        key={it.href}
                        href={it.href}
                        className="block rounded-md px-4 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/10"
                        onClick={closeAll}
                      >
                        {it.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <button
                  className="w-full flex items-center justify-between rounded-lg px-4 py-3 text-sm font-medium ring-1 ring-gray-300 dark:ring-white/20 hover:bg-black/5 dark:hover:bg-white/10"
                  onClick={() => setMobileAcc(mobileAcc === "software" ? null : "software")}
                >
                  {t("nav.software")} <span className="text-lg">{mobileAcc === "software" ? "−" : "+"}</span>
                </button>
                {mobileAcc === "software" && (
                  <div className="mt-2 ml-2 space-y-1">
                    {[
                      { href: "/software#soft-planification", label: t("soft.plan.title") },
                      { href: "/software#soft-collaboration", label: t("soft.collab.title") },
                      { href: "/software#soft-logistique", label: t("soft.log.title") },
                      { href: "/software#soft-qualite", label: t("soft.qual.title") },
                    ].map((it) => (
                      <a
                        key={it.href}
                        href={it.href}
                        className="block rounded-md px-4 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/10"
                        onClick={closeAll}
                      >
                        {it.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/references" className="block rounded-lg px-4 py-3 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10" onClick={closeAll}>
                {t("nav.references")}
              </Link>
              <Link to="/ressources" className="block rounded-lg px-4 py-3 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10" onClick={closeAll}>
                {t("nav.resources")}
              </Link>
              <Link to="/recrutement" className="block rounded-lg px-4 py-3 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10" onClick={closeAll}>
                {t("nav.recruitment")}
              </Link>
              <Link to="/contact" className="block rounded-lg px-4 py-3 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10" onClick={closeAll}>
                {t("nav.contact")}
              </Link>

              <div className="pt-4 space-y-3 border-t border-black/5 dark:border-white/10">
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={loginUrl}
                    {...extLinkProps}
                    className="rounded-lg ring-1 ring-indigo-200 dark:ring-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 px-4 py-3 text-sm font-semibold text-center transition-colors"
                    onClick={closeAll}
                  >
                    {t("nav.login")}
                  </a>
                  <a
                    href={registerUrl}
                    {...extLinkProps}
                    className="rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 px-4 py-3 text-sm font-semibold text-center shadow-sm transition-colors"
                    onClick={closeAll}
                  >
                    {t("nav.register")}
                  </a>
                </div>

                <div className="flex gap-2">
                  <Link to="/contact" className={cls(p.ctaPrimary, "flex-1 text-center")} onClick={closeAll}>
                    {t("nav.quote")}
                  </Link>
                  <button
                    onClick={() => {
                      openWhatsApp();
                      closeAll();
                    }}
                    className={cls(p.ctaSecondary, "flex-1 text-center inline-flex items-center justify-center gap-2")}
                  >
                    <MessageCircleMore size={16} /> {t("btn.whatsapp")}
                  </button>
                </div>
              </div>
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
